<template>
  <div class="recensement-container">
    <!-- Header -->
    <div class="header-section">
      <h1 class="page-title">
        <i class="fas fa-qrcode"></i>
        Recensement des Usagers - Marché de Dantokpa
      </h1>
      <p class="page-subtitle">Scannez le code QR pour récupérer les informations de l'usager</p>
    </div>

    <!-- Scanner Section -->
    <div class="scanner-section" v-if="!showResults">
      <div class="scanner-container">
        <div class="scanner-box" ref="scannerBox">
          <video ref="videoElement" class="scanner-video"></video>
          <div class="scanner-overlay">
            <div class="scanner-frame"></div>
            <div class="scanner-text">
              <i class="fas fa-qrcode"></i>
              <p>Positionnez le code QR dans le cadre</p>
            </div>
          </div>
        </div>
        
        <!-- Manual Input -->
      <div class="row">
        <div class="col-md-12 col-12 col-sm-12">
          <div class="manual-input-section">
            <div class="divider">
              <span>OU</span>
            </div>
            <div class="manual-input">
              <Form ref="searchForm" @submit="searchByManualCode" :validation-schema="searchSchema">
                <div class="row"> 
                  <div class="col-md-12 col-12 col-sm-12">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label for="manualCode" class="form-label">Saisie manuelle :</label>
                      <Field
                        type="text" 
                        class="form-control shadow-none fs-md-15 text-black" 
                        name="npi"
                        placeholder="Entrez le NPI, matricule ou code emplacement"
                        v-model="npi"
                      />
                      <ErrorMessage name="npi" class="text-danger mt-2"/>
                    </div>
                  </div>
                  <div class="col-md-12 col-12 col-sm-12">
                      <div class="form-group mb-15 mb-sm-20 mb-md-25 d-flex justify-content-center">
                          <button type="submit" class="btn btn-primary shadow-none fs-md-15 text-black" :disabled="isSearching">
                              Vérifier
                              <i v-if="!isSearching" class="fas fa-search"></i>
                              <span v-else class="spinner-border spinner-border-sm" role="status"></span>
                          </button>
                      </div>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-container">
        <div class="custom-spinner">
          <div class="spinner-circle"></div>
        </div>
        <p class="loading-text">Vérification en cours...</p>
      </div>
    </div>

    <!-- Results Section -->
    <div v-if="showResults && usagerInfo" class="results-section">
      <!-- Identity Card Modal -->
      <div class="modal-overlay" @click="closeResults">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Informations de l'Usager</h3>
            <button @click="closeResults" class="close-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <IdentityCard :usagerInfo="usagerInfo" />

          <!-- Action Buttons -->
          <div class="modal-actions">
            <button @click="closeResults" class="btn btn-secondary">
              <i class="fas fa-times"></i>
              Fermer
            </button>
            <button @click="startNewScan" class="btn btn-primary">
              <i class="fas fa-qrcode"></i>
              Nouveau Scan
            </button>
            <button @click="goToRecensementForm" class="btn btn-success">
              <i class="fas fa-user-edit"></i>
              Recenser cet usager
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUnmount, ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { Form, Field, ErrorMessage } from 'vee-validate'
import * as Yup from 'yup'
import QrScanner from 'qr-scanner'
import Swal from 'sweetalert2'
import ApiService from '@/services/ApiService'
import IdentityCard from '@/components/IdentityCard.vue'

export default defineComponent({
  name: "RecensementUsager",
  components: {
    Form,
    Field,
    ErrorMessage,
    IdentityCard
  },

  setup() {
    // Router
    const router = useRouter();
    const npi = ref('');


    // Refs
    const qrScanner = ref<QrScanner | null>(null)
    const isLoading = ref(false)
    const isSearching = ref(false)
    const showResults = ref(false)
    const usagerInfo = ref<any>(null)
    const hasCamera = ref(false)
    const searchForm = ref(null)

    // Template refs
    const videoElement = ref<HTMLVideoElement>()
    const scannerBox = ref<HTMLDivElement>()

    // Validation schema with Yup
    const searchSchema = Yup.object().shape({
      npi: Yup.string()
        .required('Veuillez saisir un npi')
        .min(10, 'Le npi doit contenir au moins 10 caractères')
        .max(10, 'Le npi ne peut pas dépasser 10 caractères')
        .matches(/^[0-9]+$/, 'Le code ne peut contenir que des chiffres')
    })

    // Methods
    const initializeScanner = async () => {
      try {
        // Vérifier si la caméra est disponible
        const cameraAvailable = await QrScanner.hasCamera()
        hasCamera.value = cameraAvailable

        if (cameraAvailable && videoElement.value) {
          qrScanner.value = new QrScanner(
            videoElement.value,
            result => handleQRResult(result.data),
            {
              highlightScanRegion: true,
              highlightCodeOutline: true,
            }
          )
          
          await qrScanner.value.start()
        } else {
          showCameraError()
        }
      } catch (error) {
        console.error('Erreur lors de l\'initialisation du scanner:', error)
        showCameraError()
      }
    }

    const handleQRResult = async (data: string) => {
      isLoading.value = true
      
      try {
        await searchUsager(data)
      } catch (error) {
        console.error('Erreur lors de la recherche:', error)
        showError('Erreur lors de la recherche de l\'usager')
      } finally {
        isLoading.value = false
      }
    }

    const searchByManualCode = async (values: any, { resetForm }: any) => {
      isSearching.value = true
      
      try {
        await searchUsager(null);
        resetForm()
        npi.value = ''
      } catch (error) {
        console.error('Erreur lors de la recherche manuelle:', error)
        showError('Erreur lors de la recherche de l\'usager')
      } finally {
        isSearching.value = false
      }
    }
    const donneeAtransmettre = ref<any>(null);

    const searchUsager = async (code: string | null) => {
      try {
        
        // Désactiver le scanner pendant la recherche
        if (qrScanner.value) {
          qrScanner.value.stop()
        }
        
        // Activer le loader
        isLoading.value = true
        
        // Appel à l'API pour rechercher l'usager
        const response = await ApiService.post(`/recensement-usager/recherche`, { code: code, npi:( npi.value ? npi.value:null) })

        if (response.data.success && response.data.data) {
          usagerInfo.value = response.data.data
          donneeAtransmettre.value = response.data.data
          showResults.value = true
        } else {
          // Aucun usager trouvé, proposer l'enregistrement
          await showRegistrationPrompt(code)
        }
      } catch (error: any) {
        if (error.response && error.response.status === 404) {
          // Aucun usager trouvé
          await showRegistrationPrompt(code)
        } else {
          throw error
        }
      } finally {
        // Réactiver le scanner et désactiver le loader
        isLoading.value = false
        if (qrScanner.value && !showResults.value) {
          qrScanner.value.start()
        }
      }
    }

    const showRegistrationPrompt = async (code: string | null) => {
      const result = await Swal.fire({
        title: 'Usager non trouvé',
        text: `Aucun usager trouvé avec le code "${code || 'NPI saisi'}". Voulez-vous l'enregistrer ?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, enregistrer',
        cancelButtonText: 'Non, annuler',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      })

      if (result.isConfirmed) {
        router.push({
          name: 'RecensementForm',
          query: code ? { code: code } : {}
        })
      }
    }

    const closeResults = () => {
      showResults.value = false
      usagerInfo.value = null
      // Redémarrer le scanner après avoir fermé le modal
      if (qrScanner.value) {
        qrScanner.value.start()
      }
    }

    const startNewScan = () => {
      showResults.value = false
      usagerInfo.value = null
      donneeAtransmettre.value = null
      npi.value = ''
      if (qrScanner.value) {
        qrScanner.value.start()
      }
    }

    const goToRecensementForm = () => {
      if (donneeAtransmettre.value) {
        // Fermer le modal
        closeResults()
        
        // Naviguer vers le formulaire de recensement avec les données de l'usager
        router.push({
          name: 'FicheRecensementPage',
          query: {
            usagerData: JSON.stringify(donneeAtransmettre.value)
          }
        })
      }
    }

    const showCameraError = () => {
      Swal.fire({
        title: 'Caméra non disponible',
        text: 'Votre appareil ne dispose pas d\'une caméra ou l\'accès a été refusé. Vous pouvez utiliser la saisie manuelle.',
        icon: 'warning',
        confirmButtonText: 'OK'
      })
    }

    const showError = (message: string) => {
      Swal.fire({
        title: 'Erreur',
        text: message,
        icon: 'error',
        confirmButtonText: 'OK'
      })
    }

    const formatDate = (date: any) => {
      if (!date) return 'Non renseigné'
      return new Date(date).toLocaleDateString('fr-FR')
    }

    // Lifecycle hooks
    onMounted(async () => {
      await initializeScanner()
    })

    onBeforeUnmount(() => {
      if (qrScanner.value) {
        qrScanner.value.destroy()
      }
    })

    return {
      // Refs
      qrScanner,
      isLoading,
      isSearching,
      showResults,
      usagerInfo,
      hasCamera,
      searchForm,
      videoElement,
      scannerBox,
      npi,
      searchSchema,
      
      // Methods
      initializeScanner,
      handleQRResult,
      searchByManualCode,
      searchUsager,
      showRegistrationPrompt,
      closeResults,
      startNewScan,
      goToRecensementForm,
      showCameraError,
      showError,
      formatDate
    }
  }
})
</script>

<style scoped>
.recensement-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.header-section {
  text-align: center;
  margin-bottom: 30px;
  color: white;
}

.page-title {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
}

.scanner-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.scanner-container {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  max-width: 600px;
  width: 100%;
}

.scanner-box {
  position: relative;
  width: 100%;
  max-width: 400px;
  margin: 0 auto 30px;
  border-radius: 15px;
  overflow: hidden;
  background: #f8f9fa;
}

.scanner-video {
  width: 100%;
  height: 300px;
  object-fit: cover;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0,0,0,0.3);
}

.scanner-frame {
  width: 200px;
  height: 200px;
  border: 3px solid #fff;
  border-radius: 15px;
  position: relative;
}

.scanner-frame::before,
.scanner-frame::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid #007bff;
}

.scanner-frame::before {
  top: -3px;
  left: -3px;
  border-right: none;
  border-bottom: none;
}

.scanner-frame::after {
  bottom: -3px;
  right: -3px;
  border-left: none;
  border-top: none;
}

.scanner-text {
  color: white;
  text-align: center;
  margin-top: 20px;
}

.scanner-text i {
  font-size: 2rem;
  margin-bottom: 10px;
  display: block;
}

.manual-input-section {
  text-align: center;
}

.divider {
  position: relative;
  margin: 20px 0;
  color: #6c757d;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #dee2e6;
}

.divider span {
  background: white;
  padding: 0 15px;
  position: relative;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.input-group {
  display: flex;
  gap: 10px;
  max-width: 400px;
  margin: 0 auto;
}

.form-control {
  flex: 1;
  padding: 12px 15px;
  border: 2px solid #dee2e6;
  border-radius: 10px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0,123,255,0.1);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.btn {
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

.text-danger {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.loading-container {
  text-align: center;
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.custom-spinner {
  margin: 0 auto 20px;
  width: 60px;
  height: 60px;
  position: relative;
}

.spinner-circle {
  width: 60px;
  height: 60px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
  font-weight: 500;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 20px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.3s;
}

.close-btn:hover {
  background: #f8f9fa;
  color: #333;
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  padding: 20px 30px;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 0 0 20px 20px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  
  .scanner-container {
    padding: 20px;
    margin: 0 10px;
  }
  
  .identity-card {
    flex-direction: column;
    padding: 20px;
  }
  
  .photo-container {
    width: 120px;
    height: 120px;
    margin: 0 auto;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .input-group {
    flex-direction: row;
    gap: 8px;
  }
  
  .form-control {
    min-width: 0;
    flex: 1;
  }
  
  .btn {
    flex-shrink: 0;
    padding: 12px 16px;
  }
}

@media (max-width: 480px) {
  .recensement-container {
    padding: 10px;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .scanner-video {
    height: 250px;
  }
  
  .scanner-frame {
    width: 150px;
    height: 150px;
  }
  
  .input-group {
    flex-direction: column;
    gap: 10px;
  }
  
  .form-control {
    width: 100%;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
